import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Users, Music, Music2, ChevronLeft, ChevronRight, ExternalLink, Search, Filter, Play, Pause, Check, X, ScanFace, FileText, Trash2, ExternalLink as LinkIcon, RotateCcw, Bot, Info, ScrollText } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { rawFetch, getTrackByRef, logout as apiLogout, adminListArtists, adminListTracks, adminListApplications, adminApproveApplication, adminDeleteApplication, adminRetryTrack, adminOverrideTrack, adminOverrideContentId } from '../../services/api';
import AdminDashboardNav from '../../components/dashboard/AdminDashboardNav';
import TrackAuditModal from '../../components/dashboard/TrackAuditModal';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
};

interface Artist {
  id: number;
  slug: string;
  artistName: string;
  ownerEmail: string;
  profileUrl: string | null;
  createdAt: number;
}

interface Track {
  id: number;
  title: string;
  verificationStatus: string;
  contentIdStatus: string;
  contentIdMatch: {
    status?: string;
    artist?: string;
    title?: string;
    album?: string | null;
    label?: string | null;
    isrc?: string | null;
    releaseDate?: string | null;
    score?: number | null;
    durationMs?: number | null;
    acrid?: string | null;
    noMatch?: boolean;
    overriddenBy?: string;
    overriddenAt?: string;
  } | null;
  artistName: string;
  tenantSlug: string;
  createdAt: number;
  trackReference: string;
  dawRequestSent: number;
  coverArtUrl: string | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface Application {
  id: number;
  artistReference: string;
  artistName: string;
  email: string;
  primaryMusicLink: string;
  socialProfileUrl: string | null;
  sourceRef: string | null;
  status: string;
  createdAt: number;
  note: string | null;
}

export default function AdminDashboard() {
  const { user, token, logout } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'artists' | 'tracks' | 'applications'>('artists');

  // Artists state
  const [artists, setArtists] = useState<Artist[]>([]);
  const [artistsPagination, setArtistsPagination] = useState<Pagination>({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [artistsLoading, setArtistsLoading] = useState(true);

  // Tracks state
  const [tracks, setTracks] = useState<Track[]>([]);
  const [tracksPagination, setTracksPagination] = useState<Pagination>({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [tracksLoading, setTracksLoading] = useState(true);
  const [trackStatus, setTrackStatus] = useState('FLAGGED');
  const [trackContentIdStatus, setTrackContentIdStatus] = useState('');
  const [trackSearch, setTrackSearch] = useState('');

  // Applications state
  const [applications, setApplications] = useState<Application[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [applicationsPagination, setApplicationsPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [appActionLoading, setAppActionLoading] = useState<string | null>(null);
  const [expandedAppId, setExpandedAppId] = useState<number | null>(null);

  // Admin track actions state
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Content ID modal state
  const [contentIdModalTrack, setContentIdModalTrack] = useState<Track | null>(null);

  // Track Audit modal state
  const [auditModalTrack, setAuditModalTrack] = useState<Track | null>(null);

  // Audio playback state
  const [playingTrackRef, setPlayingTrackRef] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loadingTrackRef, setLoadingTrackRef] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlayPause = useCallback(async (trackReference: string) => {
    // If clicking the same track that's currently loaded
    if (playingTrackRef === trackReference && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }

    setLoadingTrackRef(trackReference);
    setIsPlaying(false);
    setPlayingTrackRef(null);

    try {
      const res = await getTrackByRef(trackReference);
      const json = await res.json() as any;
      if (!res.ok || !json.data?.streamUrl) {
        console.error('Failed to get stream URL');
        setLoadingTrackRef(null);
        return;
      }

      const audio = new Audio(json.data.streamUrl);
      audioRef.current = audio;

      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        audio.currentTime = 0;
      });

      audio.addEventListener('pause', () => {
        setIsPlaying(false);
      });

      audio.addEventListener('play', () => {
        setIsPlaying(true);
      });

      await audio.play();
      setPlayingTrackRef(trackReference);
      setIsPlaying(true);
    } catch (err) {
      console.error('Playback error:', err);
    } finally {
      setLoadingTrackRef(null);
    }
  }, [playingTrackRef, isPlaying]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  // Admin track actions
  const handleTrackAction = async (trackId: number, action: 'approve' | 'reject' | 'proof-of-humanity' | 'retry' | 'override' | 'override-content-id') => {
    setActionLoading(`${trackId}-${action}`);
    try {
      let res: Response;
      if (action === 'retry') {
        res = await adminRetryTrack(trackId, token!);
      } else if (action === 'override') {
        res = await adminOverrideTrack(trackId, token!);
      } else if (action === 'override-content-id') {
        res = await adminOverrideContentId(trackId, token!);
      } else {
        res = await rawFetch(`/api/public/admin/tracks/${trackId}/${action}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      if (res.ok) {
        // Refresh the track list after action
        fetchTracks(tracksPagination.page);
      } else {
        console.error(`Failed to ${action} track`);
      }
    } catch (err) {
      console.error(`Error during ${action}:`, err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = async () => {
    try {
      await apiLogout(token!);
    } catch {}
    logout();
    window.location.href = '/admin';
  };

  // Fetch artists
  const fetchArtists = async (page: number = 1) => {
    setArtistsLoading(true);
    try {
      const res = await adminListArtists(page, token!);
      const data = await res.json() as any;
      if (res.ok) {
        setArtists(data.data);
        setArtistsPagination(data.pagination);
      }
    } catch (err) {
      console.error('Failed to fetch artists:', err);
    } finally {
      setArtistsLoading(false);
    }
  };

  // Fetch tracks
  const fetchTracks = async (page: number = 1) => {
    setTracksLoading(true);
    try {
      const res = await adminListTracks({ page, status: trackStatus || undefined, contentIdStatus: trackContentIdStatus || undefined, search: trackSearch || undefined }, token!);
      const data = await res.json() as any;
      if (res.ok) {
        setTracks(data.data);
        setTracksPagination(data.pagination);
      }
    } catch (err) {
      console.error('Failed to fetch tracks:', err);
    } finally {
      setTracksLoading(false);
    }
  };

  // Fetch applications
  const fetchApplications = async (page: number = 1) => {
    setApplicationsLoading(true);
    try {
      const res = await adminListApplications(token!);
      const data = await res.json() as any;
      if (res.ok) {
        setApplications(data.data);
        setApplicationsPagination(data.pagination);
      }
    } catch (err) {
      console.error('Failed to fetch applications:', err);
    } finally {
      setApplicationsLoading(false);
    }
  };

  // Approve application
  const handleApproveApplication = async (id: number) => {
    setAppActionLoading(`approve-${id}`);
    try {
      const res = await adminApproveApplication(id, token!);
      if (res.ok) {
        fetchApplications(applicationsPagination.page);
      }
    } catch (err) {
      console.error('Failed to approve application:', err);
    } finally {
      setAppActionLoading(null);
    }
  };

  // Delete application
  const handleDeleteApplication = async (id: number) => {
    setAppActionLoading(`delete-${id}`);
    try {
      const res = await adminDeleteApplication(id, token!);
      if (res.ok) {
        fetchApplications(applicationsPagination.page);
      }
    } catch (err) {
      console.error('Failed to delete application:', err);
    } finally {
      setAppActionLoading(null);
    }
  };

  useEffect(() => {
    if (activeTab === 'artists') fetchArtists();
    else if (activeTab === 'tracks') fetchTracks();
    else if (activeTab === 'applications') fetchApplications();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'tracks') fetchTracks();
  }, [trackStatus, trackContentIdStatus, trackSearch]);

  const statusColors: Record<string, string> = {
    PROCESSING: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
    REJECTED: 'text-red-400 bg-red-400/10 border-red-400/20',
    FLAGGED: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
    VERIFIED: 'text-green-400 bg-green-400/10 border-green-400/20',
    PENDING: 'text-gray-400 bg-gray-400/10 border-gray-400/20',
    ACCEPTED: 'text-green-400 bg-green-400/10 border-green-400/20',
  };

  return (
    <div className="min-h-screen bg-deep-charcoal">
      <AdminDashboardNav currentPage="dashboard" />

      {/* Main content */}
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Tab navigation */}
        <div className="flex gap-1 mb-8 border-b border-white/10 pb-px">
          <button
            onClick={() => setActiveTab('artists')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'artists'
                ? 'text-electric-cyan border-b-2 border-electric-cyan bg-electric-cyan/5'
                : 'text-text-secondary hover:text-white'
            }`}
          >
            <Users className="w-4 h-4" />
            Artists
          </button>
          <button
            onClick={() => setActiveTab('tracks')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'tracks'
                ? 'text-electric-cyan border-b-2 border-electric-cyan bg-electric-cyan/5'
                : 'text-text-secondary hover:text-white'
            }`}
          >
            <Music className="w-4 h-4" />
            Tracks
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'applications'
                ? 'text-electric-cyan border-b-2 border-electric-cyan bg-electric-cyan/5'
                : 'text-text-secondary hover:text-white'
            }`}
          >
            <FileText className="w-4 h-4" />
            Applications
          </button>
        </div>

        {/* Artists Tab */}
        {activeTab === 'artists' && (
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <div className="glass-card overflow-hidden">
              <div className="p-4 border-b border-white/5">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Users className="w-5 h-5 text-electric-cyan" />
                  Artists
                  <span className="text-sm text-text-muted font-normal">({artistsPagination.total} total)</span>
                </h2>
              </div>

              {artistsLoading ? (
                <div className="p-8 text-center text-text-muted">Loading...</div>
              ) : artists.length === 0 ? (
                <div className="p-8 text-center text-text-muted">No artists registered yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/5">
                        <th className="text-left text-xs font-mono text-text-muted uppercase tracking-wider px-4 py-3">Profile</th>
                        <th className="text-left text-xs font-mono text-text-muted uppercase tracking-wider px-4 py-3">Artist Name</th>
                        <th className="text-left text-xs font-mono text-text-muted uppercase tracking-wider px-4 py-3">Tenant ID</th>
                        <th className="text-right text-xs font-mono text-text-muted uppercase tracking-wider px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {artists.map((artist) => (
                        <tr key={artist.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                          <td className="px-4 py-3">
                            <img
                              src={artist.profileUrl || '/fans-audio-default-profile.png'}
                              alt={artist.artistName}
                              className="w-10 h-10 rounded-full object-cover bg-surface"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-white">{artist.artistName}</div>
                            <div className="text-xs text-text-muted">{artist.ownerEmail}</div>
                          </td>
                          <td className="px-4 py-3">
                            <code className="text-sm font-mono text-electric-cyan">{artist.slug}</code>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <a
                              href={`/dashboard?as=${encodeURIComponent(artist.slug)}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded bg-electric-cyan/10 text-electric-cyan hover:bg-electric-cyan/20 border border-electric-cyan/20 transition-colors"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              View Dashboard
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {artistsPagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
                  <span className="text-xs text-text-muted">
                    Page {artistsPagination.page} of {artistsPagination.totalPages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => fetchArtists(artistsPagination.page - 1)}
                      disabled={artistsPagination.page <= 1}
                      className="p-1.5 rounded bg-surface border border-white/10 disabled:opacity-30 hover:border-electric-cyan/30 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => fetchArtists(artistsPagination.page + 1)}
                      disabled={artistsPagination.page >= artistsPagination.totalPages}
                      className="p-1.5 rounded bg-surface border border-white/10 disabled:opacity-30 hover:border-electric-cyan/30 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Tracks Tab */}
        {activeTab === 'tracks' && (
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <div className="glass-card overflow-hidden">
              <div className="p-4 border-b border-white/5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Music className="w-5 h-5 text-electric-cyan" />
                  Tracks
                  <span className="text-sm text-text-muted font-normal">({tracksPagination.total})</span>
                </h2>

                <div className="flex flex-wrap items-center gap-3">
                  {/* Search */}
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                      type="text"
                      value={trackSearch}
                      onChange={(e) => setTrackSearch(e.target.value)}
                      placeholder="Search artist or title..."
                      className="pl-9 pr-3 py-2 text-sm bg-surface border border-white/10 rounded-lg text-white placeholder-text-muted focus:border-electric-cyan/50 focus:outline-none w-48"
                    />
                  </div>

                  {/* AI Status Filter */}
                  <div className="flex items-center gap-1">
                    <Filter className="w-4 h-4 text-text-muted" />
                    <select
                      value={trackStatus}
                      onChange={(e) => setTrackStatus(e.target.value)}
                      className="text-sm bg-surface border border-white/10 rounded-lg text-white px-3 py-2 focus:border-electric-cyan/50 focus:outline-none"
                    >
                      <option value="FLAGGED">AI: Flagged</option>
                      <option value="">AI: All</option>
                      <option value="PROCESSING">AI: Processing</option>
                      <option value="REJECTED">AI: Rejected</option>
                      <option value="VERIFIED">AI: Verified</option>
                    </select>
                  </div>

                  {/* Content ID Status Filter */}
                  <div className="flex items-center gap-1">
                    <Music2 className="w-4 h-4 text-text-muted" />
                    <select
                      value={trackContentIdStatus}
                      onChange={(e) => setTrackContentIdStatus(e.target.value)}
                      className="text-sm bg-surface border border-white/10 rounded-lg text-white px-3 py-2 focus:border-electric-cyan/50 focus:outline-none"
                    >
                      <option value="">CID: All</option>
                      <option value="PENDING">CID: Pending</option>
                      <option value="ACCEPTED">CID: Accepted</option>
                      <option value="REJECTED">CID: Rejected</option>
                    </select>
                  </div>
                </div>
              </div>

              {tracksLoading ? (
                <div className="p-8 text-center text-text-muted">Loading...</div>
              ) : tracks.length === 0 ? (
                <div className="p-8 text-center text-text-muted">No tracks matching current filters.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/5">
                        <th className="text-left text-xs font-mono text-text-muted uppercase tracking-wider px-4 py-3 w-10"></th>
                        <th className="text-left text-xs font-mono text-text-muted uppercase tracking-wider px-4 py-3 w-14">Art</th>
                        <th className="text-left text-xs font-mono text-text-muted uppercase tracking-wider px-4 py-3">Artist</th>
                        <th className="text-left text-xs font-mono text-text-muted uppercase tracking-wider px-4 py-3">Track Title</th>
                        <th className="text-left text-xs font-mono text-text-muted uppercase tracking-wider px-4 py-3">Verification</th>
                        <th className="text-right text-xs font-mono text-text-muted uppercase tracking-wider px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tracks.map((track) => (
                        <tr key={track.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handlePlayPause(track.trackReference)}
                              disabled={loadingTrackRef === track.trackReference}
                              className="w-8 h-8 flex items-center justify-center rounded-full bg-electric-cyan/10 border border-electric-cyan/20 hover:bg-electric-cyan/20 transition-colors disabled:opacity-50"
                            >
                              {loadingTrackRef === track.trackReference ? (
                                <span className="w-3 h-3 border-2 border-electric-cyan border-t-transparent rounded-full animate-spin" />
                              ) : playingTrackRef === track.trackReference && isPlaying ? (
                                <Pause className="w-3.5 h-3.5 text-electric-cyan" />
                              ) : (
                                <Play className="w-3.5 h-3.5 text-electric-cyan ml-0.5" />
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <div className="w-10 h-10 rounded overflow-hidden bg-surface border border-white/10 flex-shrink-0">
                              {track.coverArtUrl ? (
                                <img src={track.coverArtUrl} alt={track.title} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Music className="w-4 h-4 text-text-muted" />
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-white">{track.artistName}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-text-secondary">{track.title}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              <span className={`inline-block px-2 py-0.5 text-xs font-mono rounded border ${statusColors[track.verificationStatus] || 'text-text-muted'}`}>
                                AI: {track.verificationStatus}
                              </span>
                              <span className={`inline-block px-2 py-0.5 text-xs font-mono rounded border ${statusColors[track.contentIdStatus] || 'text-text-muted'}`}>
                                CID: {track.contentIdStatus || 'PENDING'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {/* ── AI Verification Group ── */}
                              {/* AI Override (green robot) - for any non-VERIFIED AI status */}
                              {track.verificationStatus !== 'VERIFIED' && (
                                <button
                                  onClick={() => handleTrackAction(track.id, 'override')}
                                  disabled={actionLoading === `${track.id}-override`}
                                  title="Override AI verification — force verify"
                                  className="w-7 h-7 flex items-center justify-center rounded bg-green-500/10 border border-green-500/20 hover:bg-green-500/20 transition-colors disabled:opacity-50"
                                >
                                  {actionLoading === `${track.id}-override` ? (
                                    <span className="w-3 h-3 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <Bot className="w-3.5 h-3.5 text-green-400" />
                                  )}
                                </button>
                              )}
                              {/* Proof of Humanity (ScanFace) - only for FLAGGED AI */}
                              {track.verificationStatus === 'FLAGGED' && (
                                <button
                                  onClick={() => handleTrackAction(track.id, 'proof-of-humanity')}
                                  disabled={actionLoading === `${track.id}-proof-of-humanity` || !!track.dawRequestSent}
                                  title={track.dawRequestSent ? "Proof of humanity already requested" : "Request proof of humanity"}
                                  className={`w-7 h-7 flex items-center justify-center rounded border transition-colors disabled:opacity-50 ${track.dawRequestSent ? 'bg-orange-500/5 border-orange-500/10 cursor-not-allowed' : 'bg-orange-500/10 border-orange-500/20 hover:bg-orange-500/20'}`}
                                >
                                  {actionLoading === `${track.id}-proof-of-humanity` ? (
                                    <span className="w-3 h-3 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <ScanFace className="w-3.5 h-3.5 text-orange-400" />
                                  )}
                                </button>
                              )}

                              {/* Divider between AI and Content ID groups */}
                              {(track.verificationStatus !== 'VERIFIED' || (track.contentIdStatus && track.contentIdStatus !== 'ACCEPTED')) && (
                                <span className="w-px h-5 bg-white/10 mx-0.5" />
                              )}

                              {/* ── Content ID Group ── */}
                              {/* View Content ID info */}
                              {track.contentIdMatch && (
                                <button
                                  onClick={() => setContentIdModalTrack(track)}
                                  title="View Content ID data"
                                  className="w-7 h-7 flex items-center justify-center rounded bg-cyan-500/10 border border-cyan-500/20 hover:bg-cyan-500/20 transition-colors"
                                >
                                  <Info className="w-3.5 h-3.5 text-cyan-400" />
                                </button>
                              )}
                              {/* Content ID Override (music notes) - when contentIdStatus is not ACCEPTED */}
                              {track.contentIdStatus && track.contentIdStatus !== 'ACCEPTED' && (
                                <button
                                  onClick={() => handleTrackAction(track.id, 'override-content-id')}
                                  disabled={actionLoading === `${track.id}-override-content-id`}
                                  title="Override Content ID — force accept"
                                  className="w-7 h-7 flex items-center justify-center rounded bg-purple-500/10 border border-purple-500/20 hover:bg-purple-500/20 transition-colors disabled:opacity-50"
                                >
                                  {actionLoading === `${track.id}-override-content-id` ? (
                                    <span className="w-3 h-3 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <Music2 className="w-3.5 h-3.5 text-purple-400" />
                                  )}
                                </button>
                              )}

                              {/* Divider before destructive actions */}
                              {track.verificationStatus === 'FLAGGED' && (
                                <span className="w-px h-5 bg-white/10 mx-0.5" />
                              )}

                              {/* Reject - only for FLAGGED */}
                              {track.verificationStatus === 'FLAGGED' && (
                                <button
                                  onClick={() => handleTrackAction(track.id, 'reject')}
                                  disabled={actionLoading === `${track.id}-reject`}
                                  title="Reject track"
                                  className="w-7 h-7 flex items-center justify-center rounded bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                                >
                                  {actionLoading === `${track.id}-reject` ? (
                                    <span className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <X className="w-3.5 h-3.5 text-red-400" />
                                  )}
                                </button>
                              )}
                              {/* Retry - for PROCESSING and REJECTED */}
                              {(track.verificationStatus === 'PROCESSING' || track.verificationStatus === 'REJECTED') && (
                                <button
                                  onClick={() => handleTrackAction(track.id, 'retry')}
                                  disabled={actionLoading === `${track.id}-retry`}
                                  title="Retry verification"
                                  className="w-7 h-7 flex items-center justify-center rounded bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
                                >
                                  {actionLoading === `${track.id}-retry` ? (
                                    <span className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <RotateCcw className="w-3.5 h-3.5 text-blue-400" />
                                  )}
                                </button>
                              )}
                              {/* Audit Trail button */}
                              <span className="w-px h-5 bg-white/10 mx-0.5" />
                              <button
                                onClick={() => setAuditModalTrack(track)}
                                title="View audit trail"
                                className="w-7 h-7 flex items-center justify-center rounded bg-white/5 border border-white/10 hover:bg-white/10 hover:border-electric-cyan/30 transition-colors"
                              >
                                <ScrollText className="w-3.5 h-3.5 text-text-secondary" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {tracksPagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
                  <span className="text-xs text-text-muted">
                    Page {tracksPagination.page} of {tracksPagination.totalPages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => fetchTracks(tracksPagination.page - 1)}
                      disabled={tracksPagination.page <= 1}
                      className="p-1.5 rounded bg-surface border border-white/10 disabled:opacity-30 hover:border-electric-cyan/30 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => fetchTracks(tracksPagination.page + 1)}
                      disabled={tracksPagination.page >= tracksPagination.totalPages}
                      className="p-1.5 rounded bg-surface border border-white/10 disabled:opacity-30 hover:border-electric-cyan/30 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <div className="glass-card overflow-hidden">
              <div className="p-4 border-b border-white/5">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-electric-cyan" />
                  Applications
                  <span className="text-sm text-text-muted font-normal">({applicationsPagination.total} pending)</span>
                </h2>
              </div>

              {applicationsLoading ? (
                <div className="p-8 text-center text-text-muted">Loading...</div>
              ) : applications.length === 0 ? (
                <div className="p-8 text-center text-text-muted">No pending applications.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/5">
                        <th className="text-left text-xs font-mono text-text-muted uppercase tracking-wider px-4 py-3">Artist</th>
                        <th className="text-left text-xs font-mono text-text-muted uppercase tracking-wider px-4 py-3">Email</th>
                        <th className="text-left text-xs font-mono text-text-muted uppercase tracking-wider px-4 py-3">Notes</th>
                        <th className="text-left text-xs font-mono text-text-muted uppercase tracking-wider px-4 py-3">Links</th>
                        <th className="text-right text-xs font-mono text-text-muted uppercase tracking-wider px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((app) => (
                        <>
                        <tr key={app.id} className={`border-b border-white/5 hover:bg-white/[0.02] transition-colors cursor-pointer ${expandedAppId === app.id ? 'bg-white/[0.02]' : ''}`} onClick={() => setExpandedAppId(expandedAppId === app.id ? null : app.id)}>
                          <td className="px-4 py-3">
                            <span className="font-medium text-white">{app.artistName}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-text-secondary">{app.email}</span>
                          </td>
                          <td className="px-4 py-3">
                            {app.note ? (
                              <span className="text-xs text-text-secondary line-clamp-2">{app.note}</span>
                            ) : (
                              <span className="text-xs text-text-muted">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <a
                                href={app.primaryMusicLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Primary music link"
                                className="w-7 h-7 flex items-center justify-center rounded bg-electric-cyan/10 border border-electric-cyan/20 hover:bg-electric-cyan/20 transition-colors"
                              >
                                <Music className="w-3.5 h-3.5 text-electric-cyan" />
                              </a>
                              {app.socialProfileUrl && (
                                <a
                                  href={app.socialProfileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Social link"
                                  className="w-7 h-7 flex items-center justify-center rounded bg-purple-500/10 border border-purple-500/20 hover:bg-purple-500/20 transition-colors"
                                >
                                  <ExternalLink className="w-3.5 h-3.5 text-purple-400" />
                                </a>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleDeleteApplication(app.id)}
                                disabled={appActionLoading === `delete-${app.id}`}
                                title="Delete application"
                                className="w-7 h-7 flex items-center justify-center rounded bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                              >
                                {appActionLoading === `delete-${app.id}` ? (
                                  <span className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                )}
                              </button>
                              <button
                                onClick={() => handleApproveApplication(app.id)}
                                disabled={appActionLoading === `approve-${app.id}`}
                                title="Approve application"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/20 transition-colors disabled:opacity-50"
                              >
                                {appActionLoading === `approve-${app.id}` ? (
                                  <span className="w-3 h-3 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <>
                                    <Check className="w-3.5 h-3.5" />
                                    Approve
                                  </>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                        {/* Expanded detail row */}
                        {expandedAppId === app.id && (
                          <tr key={`${app.id}-detail`} className="border-b border-white/5 bg-surface/30">
                            <td colSpan={5} className="px-4 py-4">
                              <div className="space-y-4 max-w-3xl">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                  <div>
                                    <span className="text-[10px] font-mono text-electric-cyan uppercase tracking-wider block mb-1">// music_link</span>
                                    <a
                                      href={app.primaryMusicLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-electric-cyan break-all hover:underline"
                                    >
                                      {app.primaryMusicLink}
                                    </a>
                                  </div>
                                  <div>
                                    <span className="text-[10px] font-mono text-electric-cyan uppercase tracking-wider block mb-1">// social_profile</span>
                                    {app.socialProfileUrl ? (
                                      <a
                                        href={app.socialProfileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-electric-cyan break-all hover:underline"
                                      >
                                        {app.socialProfileUrl}
                                      </a>
                                    ) : (
                                      <span className="text-sm text-white font-mono">—</span>
                                    )}
                                  </div>
                                  <div>
                                    <span className="text-[10px] font-mono text-electric-cyan uppercase tracking-wider block mb-1">// source_ref</span>
                                    <span className="text-sm text-white font-mono">{app.sourceRef || '—'}</span>
                                  </div>
                                </div>

                                {app.note && (
                                  <div>
                                    <span className="text-[10px] font-mono text-electric-cyan uppercase tracking-wider block mb-1">// artist_note</span>
                                    <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap">{app.note}</p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                        </>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {applicationsPagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
                  <span className="text-xs text-text-muted">
                    Page {applicationsPagination.page} of {applicationsPagination.totalPages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => fetchApplications(applicationsPagination.page - 1)}
                      disabled={applicationsPagination.page <= 1}
                      className="p-1.5 rounded bg-surface border border-white/10 disabled:opacity-30 hover:border-electric-cyan/30 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => fetchApplications(applicationsPagination.page + 1)}
                      disabled={applicationsPagination.page >= applicationsPagination.totalPages}
                      className="p-1.5 rounded bg-surface border border-white/10 disabled:opacity-30 hover:border-electric-cyan/30 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </main>

      {/* Content ID Modal */}
      {contentIdModalTrack && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setContentIdModalTrack(null)} />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-lg bg-surface border border-white/10 rounded-xl shadow-2xl overflow-hidden"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <h3 className="text-lg font-semibold text-white">Content ID Data</h3>
                <p className="text-sm text-text-muted mt-0.5">
                  {contentIdModalTrack.artistName} — {contentIdModalTrack.title}
                </p>
              </div>
              <button
                onClick={() => setContentIdModalTrack(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors"
              >
                <X className="w-4 h-4 text-text-muted" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-4">
              {/* Status badge */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-text-muted uppercase tracking-wider">Status:</span>
                <span className={`inline-block px-2 py-0.5 text-xs font-mono rounded border ${statusColors[contentIdModalTrack.contentIdStatus] || 'text-text-muted'}`}>
                  {contentIdModalTrack.contentIdStatus || 'PENDING'}
                </span>
              </div>

              {contentIdModalTrack.contentIdMatch ? (
                <>
                  {contentIdModalTrack.contentIdMatch.overriddenBy ? (
                    /* Overridden state */
                    <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                      <p className="text-sm text-emerald-400 font-medium mb-2">Manually Overridden</p>
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-sm">
                          <span className="text-text-muted">By:</span>
                          <span className="text-white">{contentIdModalTrack.contentIdMatch.overriddenBy}</span>
                        </div>
                        {contentIdModalTrack.contentIdMatch.overriddenAt && (
                          <div className="flex justify-between text-sm">
                            <span className="text-text-muted">At:</span>
                            <span className="text-white">{new Date(contentIdModalTrack.contentIdMatch.overriddenAt).toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : contentIdModalTrack.contentIdMatch.noMatch ? (
                    /* No match found */
                    <div className="p-4 rounded-lg bg-green-500/5 border border-green-500/20">
                      <p className="text-sm text-green-400">No existing match found in Content ID database. Track is clear.</p>
                    </div>
                  ) : (
                    /* Match found - show details */
                    <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/20 space-y-3">
                      <p className="text-sm text-red-400 font-medium">Match Found in Content ID Database</p>
                      <div className="space-y-2">
                        {contentIdModalTrack.contentIdMatch.title && (
                          <div className="flex justify-between text-sm">
                            <span className="text-text-muted">Title:</span>
                            <span className="text-white font-medium">{contentIdModalTrack.contentIdMatch.title}</span>
                          </div>
                        )}
                        {contentIdModalTrack.contentIdMatch.artist && (
                          <div className="flex justify-between text-sm">
                            <span className="text-text-muted">Artist:</span>
                            <span className="text-white">{contentIdModalTrack.contentIdMatch.artist}</span>
                          </div>
                        )}
                        {contentIdModalTrack.contentIdMatch.album && (
                          <div className="flex justify-between text-sm">
                            <span className="text-text-muted">Album:</span>
                            <span className="text-white">{contentIdModalTrack.contentIdMatch.album}</span>
                          </div>
                        )}
                        {contentIdModalTrack.contentIdMatch.label && (
                          <div className="flex justify-between text-sm">
                            <span className="text-text-muted">Label:</span>
                            <span className="text-white">{contentIdModalTrack.contentIdMatch.label}</span>
                          </div>
                        )}
                        {contentIdModalTrack.contentIdMatch.isrc && (
                          <div className="flex justify-between text-sm">
                            <span className="text-text-muted">ISRC:</span>
                            <span className="text-white font-mono text-xs">{contentIdModalTrack.contentIdMatch.isrc}</span>
                          </div>
                        )}
                        {contentIdModalTrack.contentIdMatch.releaseDate && (
                          <div className="flex justify-between text-sm">
                            <span className="text-text-muted">Release Date:</span>
                            <span className="text-white">{contentIdModalTrack.contentIdMatch.releaseDate}</span>
                          </div>
                        )}
                        {contentIdModalTrack.contentIdMatch.score != null && (
                          <div className="flex justify-between text-sm">
                            <span className="text-text-muted">Confidence:</span>
                            <span className="text-white">{contentIdModalTrack.contentIdMatch.score}%</span>
                          </div>
                        )}
                        {contentIdModalTrack.contentIdMatch.durationMs != null && (
                          <div className="flex justify-between text-sm">
                            <span className="text-text-muted">Duration:</span>
                            <span className="text-white">{Math.round(contentIdModalTrack.contentIdMatch.durationMs / 1000)}s</span>
                          </div>
                        )}
                        {contentIdModalTrack.contentIdMatch.acrid && (
                          <div className="flex justify-between text-sm">
                            <span className="text-text-muted">ACR ID:</span>
                            <span className="text-white font-mono text-xs">{contentIdModalTrack.contentIdMatch.acrid}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="p-4 rounded-lg bg-gray-500/5 border border-gray-500/20">
                  <p className="text-sm text-text-muted">No Content ID data available yet. The scan may still be pending.</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-white/10 flex justify-end">
              <button
                onClick={() => setContentIdModalTrack(null)}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors text-white"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Track Audit Modal */}
      <TrackAuditModal
        trackId={auditModalTrack?.id || 0}
        trackTitle={auditModalTrack?.title || ''}
        isOpen={!!auditModalTrack}
        onClose={() => setAuditModalTrack(null)}
      />
    </div>
  );
}
